/**
 * PremiumGrantHistoryModalV2 — v2 replacement for the v1
 * PremiumGrantHistoryModal. Same plumbing (fetchPremiumGrantHistory
 * twice for the summary counts, then once more per tab/page change
 * for the rows), reskinned with v2 primitives.
 *
 *   • v2 Modal (cream card + eyebrow + title) — wide because the
 *     bulk-operations table runs 6 columns of mostly-numeric data.
 *   • SegmentedControl for the Single Grants / Bulk Operations
 *     toggle (matches the v2 tab pattern used elsewhere).
 *   • LibTable for both tabs.
 *   • Cream pager strip with page-size select + Prev / Next.
 */
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fetchPremiumGrantHistory } from '../../../../services/userServices';
import {
  Button,
  Modal,
  LibTable,
  SegmentedControl,
  cn,
} from '../../../redesign';

const GRANT_TYPES = {
  SINGLE: 'single',
  BULK:   'bulk',
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function formatTs(ts) {
  if (!ts) return '—';
  try {
    return format(new Date(ts), 'MMM d, yyyy HH:mm');
  } catch {
    return String(ts);
  }
}

export default function PremiumGrantHistoryModalV2({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_bulk_granted_users: 0,
    limit: 100,
    offset: 0,
  });
  // currentTab: 'single' or 'bulk' — string-keyed so it pairs cleanly
  // with the v2 SegmentedControl (v1 used a 0/1 index).
  const [currentTab, setCurrentTab] = useState(GRANT_TYPES.SINGLE);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [summaryData, setSummaryData] = useState({
    single_grants: 0,
    bulk_grants: 0,
  });

  // Load summary counts once when the modal opens. v1 fired two
  // requests with limit=1 — same shape here so the totals come
  // back without paying for the full row payloads.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const singleResp = await fetchPremiumGrantHistory({
          limit: 1, offset: 0, grantType: GRANT_TYPES.SINGLE,
        });
        const bulkResp = await fetchPremiumGrantHistory({
          limit: 1, offset: 0, grantType: GRANT_TYPES.BULK,
        });
        setSummaryData({
          single_grants: singleResp.data.pagination.total_count,
          bulk_grants:   bulkResp.data.metrics.total_bulk_granted_users,
        });
      } catch (e) {
        console.error('Error loading summary data:', e);
      }
    })();
  }, [open]);

  // Load the rows whenever the tab / page / page-size changes.
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const resp = await fetchPremiumGrantHistory({
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          grantType: currentTab,
        });
        setLogs(resp.data.logs || []);
        setPagination(
          resp.data.pagination || {
            total_count: 0,
            total_bulk_granted_users: 0,
            limit: rowsPerPage,
            offset: page * rowsPerPage,
          },
        );
      } catch (e) {
        console.error('Error loading tab data:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, currentTab, page, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(pagination.total_count / rowsPerPage));
  const from = pagination.total_count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, pagination.total_count);

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Premium Access"
      title="Grant history"
      maxWidth={1100}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <SegmentedControl
        value={currentTab}
        onChange={(v) => {
          setCurrentTab(v);
          setPage(0);
        }}
        shape="md"
        options={[
          { value: GRANT_TYPES.SINGLE, label: 'Single grants',    count: summaryData.single_grants },
          { value: GRANT_TYPES.BULK,   label: 'Bulk operations',  count: summaryData.bulk_grants },
        ]}
      />

      <div style={{ marginTop: 14 }}>
        {loading ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 8, padding: '40px 16px', fontSize: 13 }}
          >
            Loading history…
          </div>
        ) : logs.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 8, padding: '40px 16px', fontSize: 13 }}
          >
            No {currentTab === GRANT_TYPES.SINGLE ? 'single grants' : 'bulk operations'} yet.
          </div>
        ) : currentTab === GRANT_TYPES.SINGLE ? (
          <SingleGrantsTable logs={logs} />
        ) : (
          <BulkOperationsTable logs={logs} />
        )}

        <HistoryPager
          page={page}
          totalPages={totalPages}
          totalCount={pagination.total_count}
          rowsPerPage={rowsPerPage}
          from={from}
          to={to}
          onPrev={() => page > 0 && setPage(page - 1)}
          onNext={() => page < totalPages - 1 && setPage(page + 1)}
          onPerPage={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </div>
    </Modal>
  );
}

/* ─── Tables ────────────────────────────────────────────────── */

function SingleGrantsTable({ logs }) {
  return (
    <LibTable
      fixed
      className="border border-sand"
      style={{ borderRadius: 8, overflow: 'hidden' }}
    >
      <colgroup>
        {/* date */}
        <col style={{ width: 170 }} />
        {/* user */}
        <col style={{ width: '14%' }} />
        {/* email */}
        <col style={{ width: '18%' }} />
        {/* duration */}
        <col style={{ width: 110 }} />
        {/* reason */}
        <col />
        {/* granted by */}
        <col style={{ width: '14%' }} />
        {/* status */}
        <col style={{ width: 100 }} />
      </colgroup>
      <LibTable.Head>
        <tr>
          <LibTable.Th>Date</LibTable.Th>
          <LibTable.Th>User</LibTable.Th>
          <LibTable.Th>Email</LibTable.Th>
          <LibTable.Th>Duration</LibTable.Th>
          <LibTable.Th>Reason</LibTable.Th>
          <LibTable.Th>Granted by</LibTable.Th>
          <LibTable.Th>Status</LibTable.Th>
        </tr>
      </LibTable.Head>
      <tbody>
        {logs.map((g) => (
          <LibTable.Row key={g.id}>
            <LibTable.Td>
              <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                {formatTs(g.timestamp)}
              </span>
            </LibTable.Td>
            <LibTable.Td>
              <span className="text-navy" style={{ fontSize: 13, fontWeight: 600 }}>
                {g.user_name || '—'}
              </span>
            </LibTable.Td>
            <LibTable.Td>
              <span className="font-mono text-text-muted truncate block" style={{ fontSize: 11.5 }} title={g.user_email}>
                {g.user_email || '—'}
              </span>
            </LibTable.Td>
            <LibTable.Td>
              <span className="font-mono text-navy" style={{ fontSize: 12 }}>
                {g.duration || '—'}
              </span>
            </LibTable.Td>
            <LibTable.Td>
              <span className="text-navy" style={{ fontSize: 12.5, wordBreak: 'break-word' }}>
                {g.reason || '—'}
              </span>
            </LibTable.Td>
            <LibTable.Td>
              <span className="text-text-muted truncate block" style={{ fontSize: 12 }} title={g.granted_by}>
                {g.granted_by || '—'}
              </span>
            </LibTable.Td>
            <LibTable.Td>
              <span className="text-navy" style={{ fontSize: 12 }}>
                {g.status || '—'}
              </span>
            </LibTable.Td>
          </LibTable.Row>
        ))}
      </tbody>
    </LibTable>
  );
}

function BulkOperationsTable({ logs }) {
  return (
    <LibTable
      fixed
      className="border border-sand"
      style={{ borderRadius: 8, overflow: 'hidden' }}
    >
      <colgroup>
        <col style={{ width: 170 }} />
        <col />
        <col style={{ width: 110 }} />
        <col style={{ width: 110 }} />
        <col style={{ width: 110 }} />
        <col style={{ width: 120 }} />
      </colgroup>
      <LibTable.Head>
        <tr>
          <LibTable.Th>Date</LibTable.Th>
          <LibTable.Th>Admin</LibTable.Th>
          <LibTable.NumTh>Created</LibTable.NumTh>
          <LibTable.NumTh>Granted</LibTable.NumTh>
          <LibTable.NumTh>Errors</LibTable.NumTh>
          <LibTable.Th>Status</LibTable.Th>
        </tr>
      </LibTable.Head>
      <tbody>
        {logs.map((g) => (
          <LibTable.Row key={g.id}>
            <LibTable.Td>
              <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                {formatTs(g.timestamp)}
              </span>
            </LibTable.Td>
            <LibTable.Td>
              <span className="text-navy" style={{ fontSize: 13, fontWeight: 600 }}>
                {g.admin_name || '—'}
              </span>
            </LibTable.Td>
            <LibTable.NumTd muted={!Number(g.created_count)}>
              {Number(g.created_count) || 0}
            </LibTable.NumTd>
            <LibTable.NumTd muted={!Number(g.granted_count)}>
              {Number(g.granted_count) || 0}
            </LibTable.NumTd>
            <LibTable.NumTd muted={!Number(g.error_count)}>
              <span className={Number(g.error_count) > 0 ? 'text-coral' : ''}>
                {Number(g.error_count) || 0}
              </span>
            </LibTable.NumTd>
            <LibTable.Td>
              <span className="text-navy" style={{ fontSize: 12 }}>
                {g.status || '—'}
              </span>
            </LibTable.Td>
          </LibTable.Row>
        ))}
      </tbody>
    </LibTable>
  );
}

/* ─── Pager — same shape as the shared one but local to keep
 *  this component portable. */

function HistoryPager({ page, totalPages, totalCount, rowsPerPage, from, to, onPrev, onNext, onPerPage }) {
  return (
    <div
      className="flex items-center justify-between"
      style={{
        padding: '10px 14px',
        marginTop: 8,
        borderRadius: 8,
        background: '#FAF7F1',
        border: '1px solid #E8E0CF',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div className="flex items-center" style={{ gap: 8 }}>
        <span
          className="font-condensed font-bold uppercase text-text-muted"
          style={{ fontSize: 10.5, letterSpacing: '0.08em' }}
        >
          Page size
        </span>
        <select
          value={rowsPerPage}
          onChange={onPerPage}
          style={{
            fontFamily: 'inherit',
            fontSize: 12.5,
            padding: '4px 8px',
            border: '1px solid #E8E0CF',
            borderRadius: 6,
            background: '#FAF4EA',
            color: '#2C3E50',
          }}
        >
          {ROWS_PER_PAGE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center" style={{ gap: 14 }}>
        <span className="font-mono text-text-muted" style={{ fontSize: 12 }}>
          {from.toLocaleString()}–{to.toLocaleString()} of {totalCount.toLocaleString()}
        </span>
        <button
          type="button"
          onClick={onPrev}
          disabled={page <= 0}
          className={cn('font-condensed font-bold uppercase')}
          style={{
            fontSize: 10.5,
            letterSpacing: '0.08em',
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid #E8E0CF',
            background: page <= 0 ? '#FAF4EA' : '#FAF7F1',
            color: page <= 0 ? '#9BA4AD' : '#2C3E50',
            cursor: page <= 0 ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ← Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages - 1}
          className="font-condensed font-bold uppercase"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.08em',
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid #E8E0CF',
            background: page >= totalPages - 1 ? '#FAF4EA' : '#FAF7F1',
            color: page >= totalPages - 1 ? '#9BA4AD' : '#2C3E50',
            cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
